import React from "react";

interface OverviewTabProps {
  data: any;
  step: any;
}

export default function OverviewTab({ data, step }: OverviewTabProps) {
  const { overview, trends } = data;

  if (!overview) {
    return <div className="alert alert-info">No overview data available</div>;
  }

  // Format dates to be more readable
  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === "N/A") return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="overview-tab">
      {/* Step Information - Compact Single Line */}
      <div className="analytics-card step-info-compact">
        <div className="step-info-line">
          <span className="step-info-item">
            <strong>Step:</strong> {data.step?.name || step.name}
          </span>
          <span className="step-info-separator">•</span>
          <span className="step-info-item">
            <strong>Code:</strong> {data.step?.code || "N/A"}
          </span>
          <span className="step-info-separator">•</span>
          <span className="step-info-item">
            <strong>Duration:</strong> {formatDate(data.step?.startDate)} - {formatDate(data.step?.endDate)}
          </span>
          <span className="step-info-separator">•</span>
          <span className="step-info-item">
            <strong>Status:</strong>
            <span className={`status-badge-inline ${data.step?.status === 1 ? 'active' : 'inactive'}`}>
              {data.step?.status === 1 ? "Active" : "Inactive"}
            </span>
          </span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="analytics-card">
        <h5>📊 Key Metrics</h5>
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-icon">👥</div>
            <div className="metric-content">
              <div className="metric-label">Total Students</div>
              <div className="metric-value">{overview.totalStudents || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">🎯</div>
            <div className="metric-content">
              <div className="metric-label">Active Students</div>
              <div className="metric-value">{overview.activeStudents || 0}</div>
              <div className="metric-subtitle">
                {overview.totalStudents > 0
                  ? Math.round(
                      (overview.activeStudents / overview.totalStudents) * 100
                    )
                  : 0}
                % of total
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">⚡</div>
            <div className="metric-content">
              <div className="metric-label">Engagement Score</div>
              <div className="metric-value">
                {overview.engagementScore || 0}/100
              </div>
              <div className="metric-subtitle">
                {overview.engagementScore >= 70
                  ? "Excellent"
                  : overview.engagementScore >= 50
                    ? "Good"
                    : overview.engagementScore >= 30
                      ? "Fair"
                      : "Needs Attention"}
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">⏱️</div>
            <div className="metric-content">
              <div className="metric-label">Avg Time Spent</div>
              <div className="metric-value">
                {Math.round(overview.averageTimeSpent || 0)} min
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">✅</div>
            <div className="metric-content">
              <div className="metric-label">Resource Completion</div>
              <div className="metric-value">
                {Math.round(overview.resourceCompletionRate || 0)}%
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">📝</div>
            <div className="metric-content">
              <div className="metric-label">Test Avg Score</div>
              <div className="metric-value">
                {overview.testAverageScore > 0
                  ? Math.round(overview.testAverageScore)
                  : "N/A"}
                {overview.testAverageScore > 0 && "%"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Trends */}
      {trends && (
        <div className="analytics-card">
          <h5>📈 Activity Trends</h5>
          <div className="trends-grid">
            <div className="trend-item">
              <span className="trend-label">Peak Activity Hour:</span>
              <span className="trend-value">
                {trends.peakActivityHour}:00 - {trends.peakActivityHour + 1}:00
              </span>
            </div>
            <div className="trend-item">
              <span className="trend-label">Engagement Trend:</span>
              <span
                className={`trend-badge ${
                  trends.engagementTrend === "up"
                    ? "trend-up"
                    : trends.engagementTrend === "down"
                      ? "trend-down"
                      : "trend-stable"
                }`}
              >
                {trends.engagementTrend === "up"
                  ? "📈 Increasing"
                  : trends.engagementTrend === "down"
                    ? "📉 Decreasing"
                    : "➡️ Stable"}
              </span>
            </div>
            <div className="trend-item">
              <span className="trend-label">Last Activity:</span>
              <span className="trend-value">
                {overview.lastActivityTimestamp
                  ? new Date(overview.lastActivityTimestamp).toLocaleString()
                  : "No activity yet"}
              </span>
            </div>
          </div>

          {/* Activity by Day Chart (simplified table for now) */}
          {trends.activityByDay && trends.activityByDay.length > 0 && (
            <div className="activity-timeline">
              <h6>Recent Activity (Last 7 Days)</h6>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Views</th>
                    <th>Completions</th>
                    <th>Active Students</th>
                  </tr>
                </thead>
                <tbody>
                  {trends.activityByDay.slice(-7).map((day: any, index: number) => (
                    <tr key={index}>
                      <td>{day.date}</td>
                      <td>{day.views}</td>
                      <td>{day.completions}</td>
                      <td>{day.students}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Phase Summary */}
      {data.phases && (
        <div className="analytics-card">
          <h5>📚 Phase Summary</h5>
          <div className="phase-summary-grid">
            {Object.entries(data.phases).map(([phaseName, phaseData]: [string, any]) => (
              <div key={phaseName} className="phase-summary-card">
                <div className="phase-header">
                  <span className="phase-icon">
                    {phaseName === "pre-class"
                      ? "📖"
                      : phaseName === "in-class"
                        ? "👩‍🏫"
                        : "📝"}
                  </span>
                  <span className="phase-name">
                    {phaseName.replace("-", " ").toUpperCase()}
                  </span>
                </div>
                <div className="phase-stats">
                  <div className="phase-stat">
                    <span className="stat-label">Resources:</span>
                    <span className="stat-value">{phaseData.resourceCount || 0}</span>
                  </div>
                  <div className="phase-stat">
                    <span className="stat-label">Views:</span>
                    <span className="stat-value">{phaseData.totalViews || 0}</span>
                  </div>
                  <div className="phase-stat">
                    <span className="stat-label">Students:</span>
                    <span className="stat-value">{phaseData.uniqueStudents || 0}</span>
                  </div>
                  <div className="phase-stat">
                    <span className="stat-label">Completion:</span>
                    <span className="stat-value">
                      {Math.round(phaseData.completionRate || 0)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
